import type { SubAttachmentState } from '@/msgdeskRuntime/state'
import type { SubTemplateProps } from '@/deskUiSkeletons/rowTemplates/RowTemplateTypes'

const imageExtension = /\.(?:avif|gif|jpe?g|png|svg|webp)(?:$|[?#])/i

function attachmentLabel(attachment: SubAttachmentState) {
  return attachment.title || attachment.filename || attachment.storedName || 'Gallery image'
}

function isUsableImage(attachment: SubAttachmentState) {
  const url = attachment.src || attachment.thumbSrc
  if (!url) return false
  if (attachment.thumbSrc && (attachment.width || attachment.height)) return true
  return imageExtension.test(attachment.filename || attachment.storedName || attachment.src || '')
}

export function GallerySlideTemplate({ display, controls, slots }: SubTemplateProps) {
  const canShowImage = display.permissions.canViewContent && !display.access.isContentLocked
  const attachment = canShowImage ? display.media.attachments.find(isUsableImage) : undefined
  const imageSrc = attachment?.src || attachment?.thumbSrc
  const title = display.title || `Gallery item ${display.elId}`

  return (
    <article className={`md-gallery-slide overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm${display.isChecked ? ' ring-2 ring-violet-500' : ''}`} data-md-sub-template="gallery">
      <div className="relative aspect-square w-full overflow-hidden bg-slate-100">
        <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(135deg,#f8fafc_0%,#ede9fe_100%)] text-center">
          <div>
            <span className="mx-auto block h-12 w-12 rounded-xl border-2 border-dashed border-violet-300" aria-hidden="true" />
            <span className="mt-3 block text-xs font-semibold uppercase tracking-[0.18em] text-violet-400">
              {display.access.isContentLocked ? 'Protected photo' : 'Empty photo'}
            </span>
          </div>
        </div>
        {imageSrc ? (
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src={imageSrc}
            alt={attachment ? attachmentLabel(attachment) : title}
            onError={event => { event.currentTarget.style.display = 'none' }}
          />
        ) : null}
        <div className="absolute right-3 top-3 flex gap-2 [&_.md-sub-icon]:shadow-sm">
          {controls.actions.check}
          {controls.actions.favorite}
        </div>
      </div>
      <div className="p-4">
        <header className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0 break-words text-base [&_.md-sub-title-link]:text-base">{controls.content.title}</div>
          {controls.content.time}
        </header>
        <div className="mt-3 break-words [&_.md-sub-text]:whitespace-normal">{controls.content.text}</div>
        <footer className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
          {display.permissions.canReply ? (
            <span className="relative inline-flex h-8 items-center rounded-full bg-violet-50 px-3 text-xs font-semibold text-violet-700 [&_.md-sub-icon]:absolute [&_.md-sub-icon]:inset-0 [&_.md-sub-icon]:h-full [&_.md-sub-icon]:w-full [&_.md-sub-icon]:opacity-0">
              {controls.actions.quickReply}<span className="pointer-events-none">Comment</span>
            </span>
          ) : null}
          {controls.links.answers}
          {controls.links.submsges}
          {controls.groups.meta}
        </footer>
      </div>
      {!display.access.isContentLocked ? (
        <div className="border-t border-slate-100 bg-slate-50 p-3">
          {slots.allChildren || <p className="text-xs text-slate-400">No comments yet.</p>}
        </div>
      ) : null}
    </article>
  )
}
